/**
 * Department data utility functions
 * Provides constants and helper functions for ticket departments
 */

/**
 * Department data with display names and database values
 * Format: { label: "Display Name", value: "database_value" }
 */
export const DEPARTMENTS = [
  { label: "Technical Support", value: "Technical" },
  { label: "Installations", value: "Installations" },
  { label: "Sales", value: "Sales" },
  { label: "Billing", value: "Billing" },
  { label: "Collections", value: "Collections" },
  { label: "High Sites", value: "High Sites" },
  { label: "NOC", value: "NOC" },
];

/**
 * Get department display name from database value
 * @param {string} value - The database value
 * @returns {string} The display name or original value if not found
 */
export const getDepartmentLabel = (value) => {
  if (!value) return "";
  const department = DEPARTMENTS.find((dept) => dept.value === value);
  return department ? department.label : value;
};

/**
 * Get department database value from display name
 * @param {string} label - The display name
 * @returns {string|null} The database value or null if not found
 */
export const getDepartmentValue = (label) => {
  if (!label) return null;
  const department = DEPARTMENTS.find((dept) => dept.label === label);
  return department ? department.value : null;
};

/**
 * Get formatted options for select/dropdown components
 * @param {Object} options - Configuration options
 * @param {boolean} options.includeEmpty - Whether to include an empty option (default: false)
 * @param {string} options.emptyLabel - Label for empty option (default: "Select department")
 * @returns {Array} Array of option objects { label, value }
 */
export const getDepartmentOptions = (options = {}) => {
  const { includeEmpty = false, emptyLabel = "Select department" } = options;

  if (includeEmpty) {
    return [{ label: emptyLabel, value: "" }, ...DEPARTMENTS];
  }

  return [...DEPARTMENTS];
};

/**
 * Check if a department value is valid
 * @param {string} value - The department value to check
 * @returns {boolean} True if valid, false otherwise
 */
export const isValidDepartment = (value) => {
  if (!value) return false;
  return DEPARTMENTS.some((dept) => dept.value === value);
};

export default {
  DEPARTMENTS,
  getDepartmentLabel,
  getDepartmentValue,
  getDepartmentOptions,
  isValidDepartment,
};
